import {NetworkStatus} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useMemo, useState} from 'react';

import {BenchlingQueryResult, QueryResultType} from 'graphql-client/BenchlingQuery';
import {FONT_PROXIMA_12, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {AsyncButtonLoadingBehavior, ControlledAsyncButton} from 'ui-library/buttons/AsyncButton';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useFuzzyDate from 'ui-library/hooks/useFuzzyDate';

/* exported for testing */
export type BenchlingQueryResultProps = Pick<
  BenchlingQueryResult<unknown>,
  'refetch' | 'networkStatus' | 'type'
>;

/**
 * Hook to track the last refetched time and the result type of the query.
 *
 * @param result - The type and networkStatus from the result of the query.
 * @returns The result type and the last refetched time.
 */
export function useRefetchStatus(result: {type: QueryResultType; networkStatus: NetworkStatus}): {
  type: QueryResultType;
  lastRefetchedAt: Date | null;
} {
  const [lastRefetchedAt, setLastRefetchedAt] = useState<Date | null>(null);
  useEffect(() => {
    setLastRefetchedAt(result.type === QueryResultType.SUCCESS ? new Date() : null);
  }, [result.type, result.networkStatus]);
  return useMemo(() => ({type: result.type, lastRefetchedAt}), [result.type, lastRefetchedAt]);
}

export default function LastRefetchedHeader({result}: {result: BenchlingQueryResultProps}): JSX.Element {
  const {lastRefetchedAt} = useRefetchStatus(result);
  const fuzzyDate = useFuzzyDate(lastRefetchedAt?.toISOString() ?? '');
  const text = lastRefetchedAt ? `Last refreshed ${fuzzyDate}` : 'Refreshing...';

  return (
    <div className={css(styles.refetchHeaderContainer)}>
      <Tooltip text="Refresh overview">
        <ControlledAsyncButton
          onClick={async () => {
            await result.refetch();
          }}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.SMALL}
          isLoading={lastRefetchedAt == null}
          loadingBehavior={AsyncButtonLoadingBehavior.DISABLED}
          icon="refresh"
          text=""
        />
      </Tooltip>
      <div className={css(styles.lastUpdatedText)}>{text}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  refetchHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: '8px',
  },
  lastUpdatedText: {
    ...FONT_PROXIMA_12,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    lineHeight: '14px',
  },
});
